import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { Feature } from '@vi-iot/client';
import { switchMap } from 'rxjs/operators';

import { UpdateFeatureConfig } from '../_models/feature.model';

import { RichDevice } from '../devices';
import { CommissioningActions } from './commissioning.actions';
import { COMMISSIONING_STEP, RemoteDeviceCode, RemoteDeviceType } from './commissioning.model';
import { CommissioningSelectors } from './commissioning.selectors';
import {
  CommissioningDeviceAssignChannelParams,
  CommissioningDeviceAssignHeatingCircuitParams,
  CommissioningRemoteDeviceAssignHeatingCircuitParams,
  CommissioningDeviceAssignRoomParams,
  CommissioningRemoteDeviceAssignVentilationParams,
} from './commissioning.types';

@Injectable({ providedIn: 'root' })
export class CommissioningStore {
  state$ = this.store.select(CommissioningSelectors.getState());
  currentCode$ = this.store.select(CommissioningSelectors.getCurrentCode());
  validateEuidResult$ = this.store.select(CommissioningSelectors.getValidateEuidResult());
  pairingStatus$ = this.store.select(CommissioningSelectors.getPairingStatus());
  deviceType$ = this.store.select(CommissioningSelectors.getDeviceType());
  activeStep$ = this.store.select(CommissioningSelectors.getActiveStep());
  loading$ = this.store.select(CommissioningSelectors.getLoadingIndicator());
  isDeviceAlreadyAdded$ = this.store.select(CommissioningSelectors.isDeviceAlreadyAdded());
  pairedDeviceId$ = this.store.select(CommissioningSelectors.getPairedDeviceId());
  isAddingDirectlyToRoom$ = this.store.select(CommissioningSelectors.isAddingDirectlyToRoom());
  deviceAssignmentParams$ = this.store.select(CommissioningSelectors.getDeviceAssignmentParams());
  assignmentResult$ = this.store.select(CommissioningSelectors.getAssignmentResult());
  isAddDeviceDisabled$ = this.store.select(CommissioningSelectors.isAddDeviceDisabled());
  assignmentValidationResult$ = this.store.select(
    CommissioningSelectors.getAssignmentValidationResultErrors()
  );

  constructor(private store: Store) {}

  pairedDeviceFhtChannels(): Observable<RichDevice[]> {
    return this.pairedDeviceId$.pipe(
      switchMap((deviceId) => {
        return this.store.select(CommissioningSelectors.getPairedDeviceFhtChannels(deviceId));
      })
    );
  }

  resetCommissioningState(): void {
    this.store.dispatch(CommissioningActions.resetCommissioningState());
  }

  selectFeature<T, K>(featureName: string): Observable<Feature<T, K>> {
    return this.store.select(CommissioningSelectors.getFeature<T, K>(featureName));
  }

  resetEuidValidation(): void {
    this.store.dispatch(CommissioningActions.resetEuidValidation());
  }

  resetDeviceAssignState(): void {
    this.store.dispatch(CommissioningActions.deviceAssign.reset());
  }

  listFeatures(): void {
    this.store.dispatch(CommissioningActions.listFeatures());
  }

  resetFeatures(): void {
    this.store.dispatch(CommissioningActions.resetFeatures());
  }

  updateFeatureProperty(
    featureName: string,
    value: any,
    propertyName: string | string[],
    paramName: string | string[],
    commandName?: string,
    config?: UpdateFeatureConfig
  ): void {
    this.store.dispatch(
      CommissioningActions.updateFeatureProperty({
        featureName,
        value,
        propertyName,
        paramName,
        commandName,
        config,
      })
    );
  }

  verifyEuid(): void {
    this.store.dispatch(CommissioningActions.verifyEuid.attempt());
  }

  deviceAssignRoom(room: CommissioningDeviceAssignRoomParams): void {
    this.store.dispatch(CommissioningActions.deviceAssign.room({ data: room }));
  }

  deviceAssignHeatingCircuit(heatingCircuit: CommissioningDeviceAssignHeatingCircuitParams): void {
    this.store.dispatch(CommissioningActions.deviceAssign.heatingCircuit({ data: heatingCircuit }));
  }

  remoteDeviceAssignHeatingCircuit(
    heatingCircuit: CommissioningRemoteDeviceAssignHeatingCircuitParams
  ): void {
    this.store.dispatch(
      CommissioningActions.deviceAssign.remoteHeatingCircuit({ data: heatingCircuit })
    );
  }

  remoteDeviceAssignVentilation(
    ventilation: CommissioningRemoteDeviceAssignVentilationParams
  ): void {
    this.store.dispatch(CommissioningActions.deviceAssign.ventilation({ data: ventilation }));
  }

  deviceAssignChannels(channels: CommissioningDeviceAssignChannelParams[]): void {
    this.store.dispatch(CommissioningActions.deviceAssign.channels({ data: channels }));
  }

  deviceAssignAttempt(): void {
    this.store.dispatch(CommissioningActions.deviceAssign.attempt());
  }

  resetUpdateFeatureProperty(): void {
    this.store.dispatch(CommissioningActions.resetUpdateFeatureProperty());
  }

  setActiveStep(step: COMMISSIONING_STEP): void {
    this.store.dispatch(CommissioningActions.setActiveStep({ step }));
  }

  changeDeviceType(deviceType: RemoteDeviceType): void {
    this.store.dispatch(CommissioningActions.changeDeviceType({ deviceType }));
  }

  initCommissioning(isSpecificRoomSelected: boolean): void {
    this.store.dispatch(CommissioningActions.initCommissioning({ isSpecificRoomSelected }));
  }

  updateStatus(): void {
    this.store.dispatch(CommissioningActions.updatePairingStatus());
  }

  setCode(code: RemoteDeviceCode): void {
    this.store.dispatch(CommissioningActions.setCode({ code }));
  }

  isDeviceNotAssignedToHeatingCircuit(deviceId: string): Observable<boolean> {
    return this.store.select(CommissioningSelectors.isDeviceNotAssignedToHeatingCircuit(deviceId));
  }

  isClimateSensorNotConnected(deviceId: string): Observable<boolean> {
    return this.store.select(CommissioningSelectors.isClimateSensorNotConnected(deviceId));
  }
}
